import React, { useState } from 'react';
import MdView from './MdView';
import { downloadReport } from './download';
import { buildPrompt } from './prompts';
import { runAudit } from './api';
import LOGO_DATA_URI from './logo';

const FIRM_NAME = 'Healthcare Management Consulting Group';

const C = {
  navy: '#0f1e2e', mid: '#162538', teal: '#2ec4b6', tealD: '#1a9e92',
  cream: '#f5f0e8', muted: '#8a9bb0', red: '#e85d5d',
  border: 'rgba(46,196,182,0.18)',
};

const IS = {
  width: '100%', background: C.navy, border: `1px solid ${C.border}`,
  borderRadius: 7, color: C.cream, fontFamily: 'inherit', fontSize: 13.5,
  padding: '10px 12px', outline: 'none', boxSizing: 'border-box',
};

const LS = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.8px',
  textTransform: 'uppercase', color: C.teal, marginBottom: 6,
};

export default function App() {
  const [rType, setRType] = useState('full');
  const [f, setF] = useState({ name: '', url: '', spec: '', pts: '', geo: '', notes: '', talking: 'yes' });
  const [phase, setPhase] = useState('form');
  const [report, setReport] = useState('');
  const [meta, setMeta] = useState({});
  const [err, setErr] = useState('');
  const [statusText, setStatusText] = useState('Preparing...');

  const upd = k => e => setF(p => ({ ...p, [k]: e.target.value }));

  async function run() {
    if (!f.name || !f.url || !f.spec || !f.pts || !f.geo) {
      setErr('Please fill in all required fields.');
      return;
    }
    setErr('');
    setPhase('loading');
    setStatusText(`Visiting ${f.url}...`);
    const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    try {
      setStatusText('Researching website and generating report...');
      const text = await runAudit(buildPrompt(f, rType === 'concise'));
      setMeta({
        title: `${f.name} — ${rType === 'concise' ? 'Audit Summary' : 'Full Audit Report'}`,
        sub: `${f.spec} · ${f.geo} · ${date}`,
        name: f.name,
        date,
      });
      setReport(text);
      setPhase('report');
    } catch (ex) {
      setPhase('form');
      setErr(`Audit failed: ${ex.message}`);
    }
  }

  const Header = () => (
    <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 16, background: 'rgba(15,30,46,0.98)' }}>
      <img src={LOGO_DATA_URI} alt="HMCG" style={{ height: 36, width: 'auto' }} />
      <div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, fontWeight: 700, color: C.cream }}>Healthcare Website Auditor</div>
        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginTop: 1 }}>{FIRM_NAME}</div>
      </div>
      <div style={{ marginLeft: 'auto', background: 'rgba(46,196,182,0.12)', border: `1px solid ${C.border}`, color: C.teal, fontSize: 11, padding: '4px 10px', borderRadius: 20, fontFamily: 'monospace' }}>
        AI-Powered
      </div>
    </div>
  );

  if (phase === 'loading') return (
    <div style={{ background: C.navy, color: C.cream, minHeight: '100vh' }}>
      <Header />
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width: 52, height: 52, border: '3px solid rgba(46,196,182,0.15)', borderTopColor: C.teal, borderRadius: '50%', animation: 'spin .9s linear infinite', margin: '0 auto 20px' }} />
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, marginBottom: 8 }}>Auditing your website…</div>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
          Claude is visiting the site and analyzing across 10 categories.<br />This takes 60–120 seconds.
        </p>
        <div style={{ display: 'inline-block', background: 'rgba(46,196,182,0.08)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 18px', fontFamily: 'monospace', fontSize: 12, color: C.teal }}>
          {statusText}
        </div>
      </div>
    </div>
  );

  if (phase === 'report') return (
    <div style={{ background: C.navy, color: C.cream, minHeight: '100vh' }}>
      <Header />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 18px 60px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, fontWeight: 700 }}>{meta.title}</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{meta.sub}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => downloadReport(report, meta.title, meta.name, meta.date)}
              style={{ padding: '8px 18px', background: `linear-gradient(135deg,${C.teal},${C.tealD})`, border: 'none', borderRadius: 7, color: C.navy, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >↓ Download Report</button>
            <button
              onClick={() => { setPhase('form'); setReport(''); }}
              style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 7, color: C.teal, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >← New Audit</button>
          </div>
        </div>
        <div style={{ margin: '0 0 16px', padding: '10px 14px', background: 'rgba(46,196,182,0.07)', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.muted }}>
          ℹ️ Download saves an HTML file with HMCG branding. Open in browser → File → Print → Save as PDF.
        </div>
        <div style={{ background: C.mid, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, lineHeight: 1.75, fontSize: 14 }}>
          <MdView text={report} />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.navy, color: C.cream, minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <Header />
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 18px 60px' }}>
        <div style={{ background: C.mid, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, fontWeight: 700, marginBottom: 5 }}>Practice Website Audit</div>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 22, lineHeight: 1.6 }}>
            Enter your practice details. Claude will visit the website and generate a full consulting report across 10 categories.
          </p>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase', color: C.teal, marginBottom: 9 }}>Report Type</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[['full', '📋 Full Audit'], ['concise', '⚡ Concise Summary']].map(([t, lbl]) => (
                <button key={t} onClick={() => setRType(t)} style={{ flex: 1, padding: 10, borderRadius: 7, border: `1px solid ${rType === t ? C.teal : C.border}`, background: rType === t ? 'rgba(46,196,182,0.12)' : 'transparent', color: rType === t ? C.teal : C.muted, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              ['Practice Name *', 'name', 'e.g. Summit Orthopedics'],
              ['Website URL *', 'url', 'https://www.example.com'],
              ['Specialty / Practice Type *', 'spec', 'e.g. Orthopedic Surgery'],
              ['Target Patient Population *', 'pts', 'e.g. Adults 40–70'],
              ['Geographic Market *', 'geo', 'e.g. Austin TX'],
            ].map(([label, key, ph]) => (
              <div key={key}>
                <label style={LS}>{label}</label>
                <input value={f[key]} onChange={upd(key)} placeholder={ph} style={IS} />
              </div>
            ))}

            <div>
              <label style={LS}>Consultant Talking Points?</label>
              <select value={f.talking} onChange={upd('talking')} style={IS}>
                <option value="yes">Yes — include for presentation</option>
                <option value="no">No — report only</option>
              </select>
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={LS}>Current Challenges / Notes (Optional)</label>
              <textarea
                value={f.notes}
                onChange={upd('notes')}
                placeholder="e.g. Low new patient conversion, outdated branding, poor mobile experience..."
                style={{ ...IS, minHeight: 70, resize: 'vertical' }}
              />
            </div>
          </div>

          <button
            onClick={run}
            style={{ marginTop: 22, width: '100%', padding: 14, background: `linear-gradient(135deg,${C.teal},${C.tealD})`, color: C.navy, border: 'none', borderRadius: 9, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Generate Audit Report →
          </button>

          {err && (
            <div style={{ background: 'rgba(232,93,93,0.1)', border: '1px solid rgba(232,93,93,0.3)', borderRadius: 9, padding: 16, color: C.red, fontSize: 13, marginTop: 14, lineHeight: 1.6 }}>
              {err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
